import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero } from "@/utils/utils";
import { Card, Divider, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDeleteComplicity, useFilterComplicities } from "../../../../common/api/queries/complicity";
import ActionTextIcon from "../../../../common/components/icons/ActionTextIcon";
import PopconfirmDeleteIcon from "../../../../common/components/icons/PopconfirmDeleteIcon";
import DeactivatedTag from "../../../../common/components/tags/DeactivatedTag";
import Ellipsis from "../../../../common/components/views/Ellipsis";
import { TableSizes } from "../../../../common/constants";
import { Complicity, ComplicityFilterPageRequest } from "../../types";

interface Props {
  searchParams: ComplicityFilterPageRequest;
  onUpdateClick: (complicity: Complicity) => void;
}

const ComplicityTableView = ({ searchParams, onUpdateClick }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, isFetching } = useFilterComplicities(searchParams);
  const deleteComplicity = useDeleteComplicity();

  const columns: ColumnsType<Complicity> = [
    {
      key: "institution",
      title: t("common.insuranceInstitution"),
      width: 175,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.institution.name}</Ellipsis>,
    },
    {
      key: "value",
      title: t("complicity.attrs.value"),
      width: 210,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.value}</Ellipsis>,
    },
    {
      key: "complicityEnum",
      title: t("complicity.enums.complicityEnum._label"),
      width: 235,
      ellipsis: { showTitle: false },
      render: (_, record) =>
        record.complicityEnum ? (
          <Ellipsis>{t("complicity.enums.complicityEnum." + record.complicityEnum)}</Ellipsis>
        ) : null,
    },
    {
      key: "deactivated",
      align: "center",
      width: 100,
      render: (_, record) => (record.deactivated ? <DeactivatedTag style={{ marginRight: 0 }} /> : null),
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 180,
      render: (_, record) => (
        <>
          <ActionTextIcon icon="edit" color="blue" text={t("common.edit")} onClick={() => onUpdateClick(record)} />

          <Divider orientation="vertical" />

          <Popconfirm
            title={t("complicity.helpers.deleteConfirm")}
            icon={<PopconfirmDeleteIcon />}
            cancelText={t("common.no")}
            okText={t("common.yes")}
            okType="danger"
            onConfirm={async () => {
              await deleteComplicity.mutateAsync(record.id);
            }}
          >
            <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <Card className="card-box">
      <Table<Complicity>
        {...tableStandardProps()}
        columns={columns}
        loading={isFetching}
        dataSource={data?.pageData}
        scroll={{ x: TableSizes.MEDIUM }}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount,
          onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
        }}
      />
    </Card>
  );
};

export default ComplicityTableView;
